import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Row,
  Col,
  Text,
  Button,
  Whisper,
  Tooltip,
  FlexboxGrid,
  Grid,
} from 'rsuite';
import { isNullUndefinedOrEmptyString, titleize } from '../../common/utils';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { initiateRenewFlow } from '../../store/slices/renew/renewThunks';
import {
  selectInitiatingRenewFlow,
  selectInitiatingRenewFlowError,
  selectRenewApplicationNumber,
} from '../../store/slices/renew/renewSelectors';
import IconIndicator from '../user-feedback/IconIndicator';
import Notice from '../user-feedback/Notice';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors';
import pluralize from 'pluralize';
import { LooseLicense } from 'src/types/licenses';
import { useResize } from '../../hooks/useResize';

interface PermitRowProps {
  license?: LooseLicense;
  ownerLabel?: string;
}

// Reusable component for mobile labels to reduce repetition
const MobileLabel: React.FC<{ label: string }> = ({ label }) => (
  <Col
    xs={24}
    mdHidden={true}
    lgHidden={true}
    xlHidden={true}
    xxlHidden={true}
  >
    <Text className="text-small text-bold">{label}</Text>
  </Col>
);

const PermitRow: React.FC<PermitRowProps> = ({ license, ownerLabel }) => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();
  const { isMobile } = useResize();

  const [showInitiateRenewFlowError, setShowInitiateRenewFlowError] =
    useState(false);
  const initiatingRenewFlow = useSelector(selectInitiatingRenewFlow);
  const licenseToBeRenewedApplicationNumber = useSelector(
    selectRenewApplicationNumber
  );
  const initiatingRenewFlowError = useSelector(selectInitiatingRenewFlowError);
  const registrationTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );

  const onFlowStepsMapped = useCallback(
    (path?: string) => {
      if (path) {
        placeNavigate(`/${path}`);
      }
    },
    [placeNavigate]
  );

  useEffect(() => {
    if (
      !initiatingRenewFlow &&
      license?.applicationNumber &&
      license.applicationNumber === licenseToBeRenewedApplicationNumber &&
      !isNullUndefinedOrEmptyString(initiatingRenewFlowError)
    ) {
      setShowInitiateRenewFlowError(true);
      const timeoutId = setTimeout(() => {
        setShowInitiateRenewFlowError(false);
      }, 10000); // 10 seconds

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      setShowInitiateRenewFlowError(false);
    }
  }, [
    initiatingRenewFlow,
    licenseToBeRenewedApplicationNumber,
    license?.applicationNumber,
    initiatingRenewFlowError,
  ]);

  const handleRenewClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      (e.target as HTMLButtonElement).blur();
      if (!initiatingRenewFlow && license?.applicationNumber) {
        dispatch(
          initiateRenewFlow(
            license.applicationNumber,
            `/all-permits`,
            onFlowStepsMapped
          )
        );
      }
    },
    [
      initiatingRenewFlow,
      license?.applicationNumber,
      dispatch,
      onFlowStepsMapped,
    ]
  );

  const isRenewingThisLicense = useMemo(
    () =>
      initiatingRenewFlow &&
      licenseToBeRenewedApplicationNumber === license?.applicationNumber,
    [
      initiatingRenewFlow,
      licenseToBeRenewedApplicationNumber,
      license?.applicationNumber,
    ]
  );

  return (
    <FlexboxGrid
      as={Row}
      align="middle"
      className="all-permits-permit-row"
    >
      <FlexboxGrid.Item
        as={Col}
        xs={24}
        md={7}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <MobileLabel label="Address" />
            <Col xs={24}>
              <Text>{titleize(license?.address || '')}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={12}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <MobileLabel label={`${registrationTypeVerbiage} Number`} />
            <Col xs={24}>
              <Text>{license?.licenseNumber ?? '-'}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={12}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <MobileLabel label="Status" />
            <Col xs={24}>
              <Text>{titleize(license?.status ?? '-')}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={12}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <MobileLabel label="Expiration Date" />
            <Col xs={24}>
              <Text>{license?.expirationDate ?? '-'}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={24}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <MobileLabel label={`${pluralize(ownerLabel ?? '')} Name`} />
            <Col xs={24}>
              {license?.ownersNameToolTip ? (
                <Whisper
                  trigger="hover"
                  speaker={<Tooltip>{license.ownersNameToolTip}</Tooltip>}
                >
                  <Text>{license?.ownersNames}</Text>
                </Whisper>
              ) : (
                <Text>{license?.ownersNames}</Text>
              )}
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={24}
        md={5}
      >
        <div role="grid">
          <FlexboxGrid.Item
            as={Row}
            style={
              isMobile
                ? { display: 'flex', flexDirection: 'column', gap: '10px' }
                : undefined
            }
          >
            <FlexboxGrid.Item
              as={Col}
              xs={24}
              sm={12}
            >
              {license?.canRenew && (
                <Button
                  style={{ width: '100%' }}
                  onClick={handleRenewClick}
                  className="btn-secondary"
                >
                  Renew
                  {isRenewingThisLicense && (
                    <IconIndicator
                      color="var(--soft-blue)"
                      size={16}
                      textStyle={{ marginLeft: '5px' }}
                    />
                  )}
                </Button>
              )}
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              as={Col}
              xs={24}
              sm={12}
            >
              <Button
                className="btn-secondary"
                style={{ width: '100%' }}
                onClick={() =>
                  placeNavigate(`/single-permit/${license?.applicationNumber}`)
                }
              >
                View
              </Button>
            </FlexboxGrid.Item>
          </FlexboxGrid.Item>
        </div>
      </FlexboxGrid.Item>
      {showInitiateRenewFlowError && (
        <FlexboxGrid.Item
          as={Col}
          xs={24}
          style={{ marginTop: '10px' }}
        >
          <Notice message={initiatingRenewFlowError} />
        </FlexboxGrid.Item>
      )}
    </FlexboxGrid>
  );
};

export default PermitRow;
